import "../adminHome.scss";
import "./superAdmin.scss";

import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../../../features/helpers";
import { getOrganizations } from "../../../../store/OrganizationSlice";
import { fetchAllDeposits } from "../../../../store/DepositsSlice";
import { formatCurrencyBRL } from "../../../../utils/format";
import {
    Building2, ArrowDownCircle, DollarSign,
    ChartCandlestick, FileText,
} from "lucide-react";
import HomeUsdt from "../../../../components/chart/home-usdt";

import {
    PageCard, SectionTitle, KpiCard, DataList, DataListColumn,
    StatusBadge, MoneyValue,
} from "../../../../components/ui";

const SuperAdminHome = () => {

    const dispatch = useAppDispatch();

    const organizations = useAppSelector((s) => s.organization.organizations);
    const allDeposits   = useAppSelector((s) => s.deposit.list);

    useEffect(() => {
        dispatch(getOrganizations());
        dispatch(fetchAllDeposits());
    }, [dispatch]);

    // ---- KPIs derivados
    const now = new Date();
    const currMonth = now.getMonth();
    const currYear  = now.getFullYear();

    const depositsThisMonth = (allDeposits ?? []).filter((d: any) => {
        const date = new Date(d.createdAt);
        return (
            date.getMonth() === currMonth &&
            date.getFullYear() === currYear &&
            (d.status === "CONFIRMED" || d.status === "COMPLETED")
        );
    });

    const totalDepositsValue = depositsThisMonth.reduce(
        (acc: number, d: any) => acc + Number(d.value ?? 0), 0
    );

    const activeOrgs = (organizations ?? []).filter((o: any) =>
        (o.status ?? "").toLowerCase() === "ativa"
    ).length;

    // ---- Tabela de organizacoes
    const orgRows = (organizations ?? []).map((org: any) => {
        const orgDeposits = depositsThisMonth.filter((d: any) => d.organization_id === org.id);
        const sumDeposits = orgDeposits.reduce((acc: number, d: any) => acc + Number(d.value ?? 0), 0);
        return {
            id: org.id,
            nome: org.nome,
            status: org.status,
            depositsCount: orgDeposits.length,
            depositsSum: sumDeposits,
        };
    });

    const orgColumns: DataListColumn<any>[] = [
        { key: "nome",   header: "Nome",            render: (o) => <strong>{o.nome}</strong> },
        { key: "status", header: "Status",          render: (o) => <StatusBadge status={o.status} /> },
        { key: "depc",   header: "Depositos (mes)", render: (o) => o.depositsCount, align: "right", mono: true },
        { key: "deps",   header: "Volume",          render: (o) => <MoneyValue amount={o.depositsSum} tone="up" />, align: "right" },
    ];

    return (
        <div className="super-admin-wrapper">

            {/* KPIs principais — primeira linha (volume financeiro) */}
            <section className="kpi-grid kpi-grid-3">
                <KpiCard variant="info"    icon={Building2}      label="Organizacoes ativas" value={activeOrgs} hint={`${organizations?.length ?? 0} no total`} />
                <KpiCard variant="success" icon={ArrowDownCircle} label="Depositos este mes" value={depositsThisMonth.length} hint="confirmados/concluidos" />
                <KpiCard variant="warn"    icon={DollarSign}     label="Volume do mes"       value={formatCurrencyBRL(totalDepositsValue)} />
            </section>

            {/* Chart + Atalhos rapidos lado a lado */}
            <div className="summary-row">
                <PageCard className="chart-block">
                    <SectionTitle icon={ChartCandlestick}>Historico de Precos</SectionTitle>
                    <HomeUsdt />
                </PageCard>

                <PageCard className="quick-links">
                    <SectionTitle icon={FileText}>Atalhos</SectionTitle>
                    <ul className="ql-list">
                        <li><a href="/users">Usuarios</a></li>
                        <li><a href="/organizations">Organizacoes</a></li>
                        <li><a href="/companies-overview">Resumo por Empresa</a></li>
                        <li><a href="/quotations">Cotacoes</a></li>
                    </ul>
                </PageCard>
            </div>

            {/* Tabela de organizacoes */}
            <PageCard className="org-table-section">
                <SectionTitle icon={Building2} count={orgRows.length}>Organizacoes</SectionTitle>
                <DataList
                    columns={orgColumns}
                    rows={orgRows}
                    empty="Nenhuma organizacao encontrada."
                />
            </PageCard>

        </div>
    );
};

export default SuperAdminHome;
